import React from "react";
import styled from "styled-components";
import { SkeletonBox, SkeletonText, SkeletonCard, SkeletonChip } from "./SkeletonBox";

/**
 * ShowcasePageSkeleton - Non-animated skeleton for showcase pages
 * (Movie Home, Series Home, Anavatan)
 * 
 * These pages have:
 * - No hero slider
 * - Multiple content rows with movie cards
 * - Optional header (Anavatan has city selector)
 */

var ContentWrapper = styled.div`
  -webkit-box-flex: 1;
  -webkit-flex: 1;
  -moz-box-flex: 1;
  -ms-flex: 1;
  flex: 1;
  overflow: hidden;
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: vertical;
  -webkit-box-direction: normal;
  -webkit-flex-direction: column;
  -moz-box-orient: vertical;
  -moz-box-direction: normal;
  -ms-flex-direction: column;
  flex-direction: column;
`;

var ScrollingRows = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-flex-shrink: 1;
  -moz-flex-shrink: 1;
  -ms-flex-negative: 1;
  flex-shrink: 1;
  -webkit-box-flex: 1;
  -webkit-flex-grow: 1;
  -moz-box-flex: 1;
  -ms-flex-positive: 1;
  flex-grow: 1;
  padding-top: 20px;
`;

var ContentRowWrapper = styled.div`
  position: relative;
  min-height: 400px;
  margin-top: 2rem;
  margin-bottom: 2rem;
  
  &::after {
    content: "";
    position: absolute;
    width: 312px;
    height: 100%;
    background: -webkit-linear-gradient(left, #121212, rgba(0, 0, 0, 0));
    background: -moz-linear-gradient(left, #121212, rgba(0, 0, 0, 0));
    background: linear-gradient(to right, #121212, rgba(0, 0, 0, 0));
    left: 0;
    top: 0;
    pointer-events: none;
  }
`;

var RowTitle = styled.div`
  height: 27px;
  width: 200px;
  margin-bottom: 22px;
  margin-right: 40px;
  margin-top: 40px;
  background-color: #262626;
  -webkit-border-radius: 4px;
  -moz-border-radius: 4px;
  border-radius: 4px;
`;

var CardsContainer = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  padding-left: 60px;
  overflow: hidden;
`;

var CardWrapper = styled.div`
  margin-left: 12px;
  -webkit-flex-shrink: 0;
  -moz-flex-shrink: 0;
  -ms-flex-negative: 0;
  flex-shrink: 0;
  
  &:last-child {
    margin-left: 0;
  }
`;

var HeaderSection = styled.div`
  padding: 20px 40px;
  background-color: #151515;
  margin-bottom: 10px;
`;

var HeaderRow = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-box-align: center;
  -webkit-align-items: center;
  -moz-box-align: center;
  -ms-flex-align: center;
  align-items: center;
  margin-bottom: 16px;
`;

var CityChipsRow = styled.div`
  display: -webkit-box;
  display: -webkit-flex;
  display: -moz-box;
  display: -ms-flexbox;
  display: flex;
  -webkit-box-orient: horizontal;
  -webkit-box-direction: normal;
  -webkit-flex-direction: row;
  -moz-box-orient: horizontal;
  -moz-box-direction: normal;
  -ms-flex-direction: row;
  flex-direction: row;
  -webkit-flex-wrap: wrap;
  -ms-flex-wrap: wrap;
  flex-wrap: wrap;
`;

var ChipWrapper = styled.div`
  margin-left: 10px;
  margin-bottom: 10px;
`;

/**
 * Content Row Skeleton (reusable)
 */
function ContentRowSkeleton({ cardType = "portrait", cardCount = 6 }) {
  var cards = [];
  for (var i = 0; i < cardCount; i++) {
    cards.push(
      <CardWrapper key={"card_" + i}>
        <SkeletonCard type={cardType} />
      </CardWrapper>
    );
  }

  return (
    <ContentRowWrapper>
      <RowTitle />
      <CardsContainer>
        {cards}
      </CardsContainer>
    </ContentRowWrapper>
  );
}

/**
 * Anavatan Header Skeleton (city selector)
 */
function AnavatanHeaderSkeleton() {
  var chips = [];
  var widths = [80, 100, 90, 110, 85, 95, 100, 80];

  for (var i = 0; i < 8; i++) {
    chips.push(
      <ChipWrapper key={"city_" + i}>
        <SkeletonChip width={widths[i]} />
      </ChipWrapper>
    );
  }

  return (
    <HeaderSection>
      <HeaderRow>
        <SkeletonText width={180} size="xl" style={{ marginLeft: 20 }} />
        <SkeletonBox width={40} height={40} borderRadius={8} />
      </HeaderRow>
      <CityChipsRow>
        {chips}
      </CityChipsRow>
    </HeaderSection>
  );
}

/**
 * ShowcasePageSkeleton - Main export
 * @param {boolean} showHeader - Show Anavatan-style header
 * @param {number} rowCount - Number of content rows to show
 */
function ShowcasePageSkeleton({ showHeader = false, rowCount = 4 }) {
  var rows = [];
  for (var i = 0; i < rowCount; i++) {
    rows.push(
      <ContentRowSkeleton
        key={"row_" + i}
        cardType="portrait"
        cardCount={6}
      />
    );
  }

  return (
    <ContentWrapper>
      <ScrollingRows>
        {showHeader && <AnavatanHeaderSkeleton />}
        {rows}
      </ScrollingRows>
    </ContentWrapper>
  );
}

export { ContentRowSkeleton, AnavatanHeaderSkeleton };
export default ShowcasePageSkeleton;
